import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../lib/api";
import { AdminLayout } from "../../components/admin/AdminLayout";
import { StatusBadge } from "../../components/StatusBadge";
import { OrderTimeline } from "../../components/OrderTimeline";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";

const STATUSES = ["Received", "On route", "Completed", "On hold"];

export default function AdminOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [cancelOpen, setCancelOpen] = useState(false);
  const { data: o } = useQuery({ queryKey: ["admin-order", id], queryFn: async () => (await api.get(`/admin/orders/${id}`)).data });
  useEffect(() => { if (o) document.title = `${o.order_number} | Sambiva Admin`; }, [o]);

  const updateStatus = async (status) => {
    try { await api.patch(`/admin/orders/${id}/status`, { status }); qc.invalidateQueries(); toast.success(`Status updated to ${status}.`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cancel = async (returnStock) => {
    try { await api.post(`/admin/orders/${id}/cancel`, { return_stock: returnStock }); qc.invalidateQueries(); setCancelOpen(false); toast.success("Order cancelled."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!o) return <AdminLayout title="Order"><div className="text-center py-16 text-sambiva-muted">Loading…</div></AdminLayout>;

  return (
    <AdminLayout title={o.order_number}>
      <button onClick={() => navigate("/admin/orders")} className="flex items-center gap-1 text-sambiva-muted hover:text-sambiva-green mb-5 text-sm"><ChevronLeft size={16} /> Orders</button>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-black/5 p-6">
            <div className="flex items-center justify-between mb-6"><div><h3 className="font-semibold text-sambiva-charcoal">{o.company_name}</h3><p className="text-sm text-sambiva-muted">{new Date(o.created_at).toLocaleString()}</p></div><StatusBadge status={o.status} /></div>
            <OrderTimeline status={o.status} history={o.status_history} />
          </div>
          <div className="bg-white rounded-2xl border border-black/5 overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-[#f6f7f9] text-sambiva-muted text-left"><tr><th className="px-4 py-3 font-semibold">Product</th><th className="px-4 py-3 font-semibold">SKU</th><th className="px-4 py-3 font-semibold">Price</th><th className="px-4 py-3 font-semibold">Qty</th><th className="px-4 py-3 font-semibold">Total</th></tr></thead>
              <tbody>
                {o.items.map((i, idx) => (
                  <tr key={idx} className="border-t border-black/5"><td className="px-4 py-3 font-medium text-sambiva-charcoal">{i.product_name}</td><td className="px-4 py-3 text-sambiva-muted">{i.sku}</td><td className="px-4 py-3">€{i.unit_price.toFixed(2)}</td><td className="px-4 py-3">{i.quantity}</td><td className="px-4 py-3 font-semibold">€{i.line_total.toFixed(2)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-black/5 p-6">
            <h3 className="font-semibold text-sambiva-charcoal mb-4">Update Status</h3>
            <div className="grid grid-cols-2 gap-2 mb-3">
              {STATUSES.map((s) => (
                <button key={s} data-testid={`set-status-${s.replace(/\s+/g, "-")}`} disabled={o.status === s || o.status === "Cancelled"} onClick={() => updateStatus(s)} className={`text-sm px-3 py-2 rounded-lg font-medium transition-colors disabled:opacity-40 ${o.status === s ? "bg-sambiva-green text-white" : "bg-sambiva-cream text-sambiva-charcoal hover:bg-sambiva-green/10"}`}>{s}</button>
              ))}
            </div>
            {o.status !== "Cancelled" && <button data-testid="cancel-order" onClick={() => setCancelOpen(true)} className="w-full text-sm bg-sambiva-red/10 text-sambiva-red font-semibold py-2 rounded-lg hover:bg-sambiva-red/20 transition-colors">Cancel Order</button>}
          </div>
          <div className="bg-white rounded-2xl border border-black/5 p-6">
            <h3 className="font-semibold text-sambiva-charcoal mb-3">Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-sambiva-muted">Subtotal</span><span>€{o.subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-sambiva-muted">VAT</span><span>€{o.vat.toFixed(2)}</span></div>
              <div className="flex justify-between pt-2 border-t border-black/8 font-semibold"><span>Total</span><span className="text-sambiva-red">€{o.total.toFixed(2)}</span></div>
            </div>
          </div>
        </div>
      </div>

      {cancelOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="cancel-modal">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="font-serif text-xl text-sambiva-charcoal mb-2">Cancel order?</h3>
            <p className="text-sm text-sambiva-muted mb-5">Return quantities to stock?</p>
            <div className="space-y-2">
              <button data-testid="cancel-return-stock" onClick={() => cancel(true)} className="w-full bg-sambiva-green text-white font-semibold py-2.5 rounded-xl text-sm">Cancel and return stock</button>
              <button data-testid="cancel-no-stock" onClick={() => cancel(false)} className="w-full bg-sambiva-red/10 text-sambiva-red font-semibold py-2.5 rounded-xl text-sm">Cancel without returning stock</button>
              <button onClick={() => setCancelOpen(false)} className="w-full border border-black/10 font-semibold py-2.5 rounded-xl text-sm">Keep order</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
